import React from 'react';

export type BreadcrumbItem = {
  key?: React.Key;
  label?: string;
  onPress?: () => void;
  href?: string;
  node?: React.ReactNode;
};

type BreadcrumbsContextType = {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
};

const BreadcrumbsContext = React.createContext<BreadcrumbsContextType | undefined>(undefined);

export const BreadcrumbsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = React.useState<BreadcrumbItem[]>([]);

  return (
    <BreadcrumbsContext.Provider value={{ breadcrumbs, setBreadcrumbs }}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbs = (breadcrumbs?: BreadcrumbItem[]) => {
  const context = React.useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbsProvider');
  }

  // If breadcrumbs are provided, update only when content meaningfully changes
  React.useEffect(() => {
    if (!breadcrumbs) return;

    const current = context.breadcrumbs;
    const isSame =
      current.length === breadcrumbs.length &&
      current.every((c, i) => {
        const b = breadcrumbs[i];

        // Compare by key if both have keys (stable comparison for nodes)
        if (c.key !== undefined && b.key !== undefined) {
          if (c.key !== b.key) return false;
          // Keys match, but also check if one switched from node to label
          if ((c.node && !b.node) || (!c.node && b.node)) return false;
          // For node-based crumbs, compare by node identity so updates propagate
          if (c.node && b.node) {
            return c.node === b.node;
          }
          // Fall through to label/href comparison
        }

        // For items without nodes, compare label and href
        if (!c.node && !b.node) {
          return c.label === b.label && (c.href ?? '') === (b.href ?? '');
        }

        // One has node, other doesn't - consider them different
        return false;
      });

    if (!isSame) {
      context.setBreadcrumbs(breadcrumbs);
    }
  }, [breadcrumbs]);

  // Clear breadcrumbs on unmount of a setter
  React.useEffect(() => {
    if (!breadcrumbs) return;
    return () => {
      context.setBreadcrumbs([]);
    };
  }, []);

  return context;
};
